import { Command } from 'commander';
import { runBootstrapLauncher } from './commands/bootstrap.js';
import { runCurateLauncher } from './commands/curate.js';
import { runCurateDedupCommand } from './commands/curate-dedup.js';
import { runCuratePersistCommand } from './commands/curate-persist.js';
import { runConflictPrepareCommand } from './commands/conflict-prepare.js';
import { runDoctor } from './commands/doctor.js';
import { runDraftsCollectCommand } from './commands/drafts-collect.js';
import { runFindDocsCommand } from './commands/finddocs.js';
import { runFreshness } from './commands/freshness.js';
import { runIndexRebuild } from './commands/index-rebuild.js';
import { runInit } from './commands/init.js';
import { runLintCommand } from './commands/lint.js';
import { runLogsPrune } from './commands/logs-prune.js';
import { runSessionLogStageLiveCommand } from './commands/session-log-stage-live.js';
import { runSessionLogUpdateProposalsCommand } from './commands/session-log-update-proposals.js';
import { runMigrateOkfV3 } from './commands/migrate-okf-v3.js';
import { runMigrateStatus } from './commands/migrate.js';
import { runNodeAddLauncher } from './commands/node-add.js';
import { runNodeWriteCommand } from './commands/node-write.js';
import { runPackExportCommand } from './commands/pack-export.js';
import { runPackImportCommand } from './commands/pack-import.js';
import { runPlaceApply, runPlaceInventory } from './commands/place.js';
import { runRebalanceMove, runRebalanceTrigger } from './commands/rebalance.js';
import { runSchemaCommand } from './commands/schema.js';
import { runStatus } from './commands/status.js';
import { runValidateCommand } from './commands/validate.js';
import { listHarnessIds } from './harnesses/registry.js';
import { log } from './lib/log.js';
import { SCHEMA_NAMES } from './lib/schema-registry.js';
import { packageVersion } from './lib/version.js';

async function main(): Promise<void> {
  const program = new Command();
  program
    .name('kenkeep')
    .description('Build and maintain a per-repo knowledge base from AI coding sessions.')
    .version(packageVersion())
    .option(
      '--harness <id>',
      `select the active harness adapter for this invocation (one of: ${listHarnessIds().join(', ')})`
    );

  const getHarnessFlag = (): string | undefined => {
    const value = program.opts<{ harness?: string }>().harness;
    return typeof value === 'string' && value.length > 0 ? value : undefined;
  };

  program
    .command('init')
    .description('First-time setup: copy templates, register harness hooks, record version.')
    .requiredOption(
      // No -h short alias: it would shadow Commander's built-in -h/--help,
      // turning `init -h` into a confusing "argument missing" error.
      '--harnesses <list>',
      `comma-separated list of harnesses to wire up (supported: ${listHarnessIds().join(', ')})`,
      (value: string) =>
        value
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
    )
    .option(
      '-u, --upgrade',
      'refresh hooks/slash commands/prompts to the current package version while preserving local overrides and config.yaml',
      false
    )
    .action(async (opts: { harnesses: string[]; upgrade: boolean }) => {
      await runInit(opts);
    });

  program
    .command('status')
    .description('Show extraction and curation queues plus kenkeep stats.')
    .action(async () => {
      process.exit(await runStatus());
    });

  program
    .command('doctor')
    .description(
      'Verify hook installation, schema validity, per-harness install status, and frontmatter hygiene.'
    )
    .option('-v, --verbose', 'show extra diagnostics', false)
    .action(async (opts: { verbose: boolean }) => {
      const code = await runDoctor({ ...opts, harness: program.opts().harness });
      process.exit(code);
    });

  program
    .command('lint')
    .description(
      'Run mechanical kenkeep content health checks (dangling edges, slug/id mismatch, tag duplicates, tag whitespace, empty folder summaries in FOLDER_SUMMARIES.md, orphans).'
    )
    .option('-v, --verbose', 'list every error and finding individually', false)
    .action(async (opts: { verbose: boolean }) => {
      const code = await runLintCommand({ verbose: opts.verbose });
      process.exit(code);
    });

  program
    .command('freshness')
    .description(
      'Deterministic, read-only advisory: report how many nodes may describe source code that changed since the node was last curated. Baselines come from git history (no stamp, no state); pure Node, no LLM. Always exits 0.'
    )
    .option('-v, --verbose', 'list every flagged node and the paths that changed', false)
    .action(async (opts: { verbose: boolean }) => {
      process.exit(await runFreshness({ verbose: opts.verbose }));
    });

  program
    .command('curate')
    .description(
      'Launch the kk-curate skill in the active harness (execs `<harness> -p "/kk-curate"`).'
    )
    .action(() => {
      const launchOpts: Parameters<typeof runCurateLauncher>[0] = {};
      const harnessFlag = getHarnessFlag();
      if (harnessFlag !== undefined) launchOpts.harness = harnessFlag;
      runCurateLauncher(launchOpts);
    });

  program
    .command('curate-dedup')
    .description(
      'Deterministic curator dedup primitive: validates a proposals JSON (--input or stdin), dedups, mints conflict ids, writes conflict files and stamps consumed session logs. Pure Node — no LLM.'
    )
    .option(
      '--input <path>',
      'path to proposals JSON (CuratorOutputSchema); reads stdin when omitted'
    )
    .option('--output <path>', 'write deduped surviving (non-conflict) actions to this JSON file')
    .option('--run-id <id>', 'caller-supplied run id (defaults to a fresh randomUUID)')
    .option('--sessions-dir <path>', 'override the _sessions directory (defaults to repo paths)')
    .option('--conflicts-dir <path>', 'override the conflicts directory (defaults to repo paths)')
    .option(
      '--session-id <uuid>',
      'stamp only the unprocessed done log matching this session id (default: all pending done logs)'
    )
    .action(
      async (opts: {
        input?: string;
        output?: string;
        runId?: string;
        sessionsDir?: string;
        conflictsDir?: string;
        sessionId?: string;
      }) => {
        const flags: Parameters<typeof runCurateDedupCommand>[0] = {};
        if (opts.input !== undefined) flags.input = opts.input;
        if (opts.output !== undefined) flags.output = opts.output;
        if (opts.runId !== undefined) flags.runId = opts.runId;
        if (opts.sessionsDir !== undefined) flags.sessionsDir = opts.sessionsDir;
        if (opts.conflictsDir !== undefined) flags.conflictsDir = opts.conflictsDir;
        if (opts.sessionId !== undefined) flags.sessionId = opts.sessionId;
        const code = await runCurateDedupCommand(flags);
        process.exit(code);
      }
    );

  program
    .command('curate-persist')
    .description(
      'Deterministic curator persistence primitive: validates a survivors JSON from curate-dedup and persists add/modify actions in one pass while reporting per-action partial failures. Pure Node, no LLM.'
    )
    .option(
      '--input <path>',
      'path to survivors JSON (CuratorOutputSchema subset); reads stdin when omitted'
    )
    .action(async (opts: { input?: string }) => {
      const flags: Parameters<typeof runCuratePersistCommand>[0] = {};
      if (opts.input !== undefined) flags.input = opts.input;
      const code = await runCuratePersistCommand(flags);
      process.exit(code);
    });

  program
    .command('bootstrap')
    .description(
      'Launch the kk-bootstrap skill in the active harness (execs `<harness> -p "/kk-bootstrap …"`). Scope is controlled by .kkignore plus an optional --from <scope>.'
    )
    .option(
      '--from <scope>',
      'narrow discovery to a subdirectory of the repo root (path relative to repo root)'
    )
    .action((opts: { from?: string }) => {
      const launchOpts: Parameters<typeof runBootstrapLauncher>[0] = {};
      if (opts.from !== undefined) launchOpts.from = opts.from;
      const harnessFlag = getHarnessFlag();
      if (harnessFlag !== undefined) launchOpts.harness = harnessFlag;
      runBootstrapLauncher(launchOpts);
    });

  // Deprecation alias for one release. Same behavior as `bootstrap`, but
  // writes a stderr notice pointing at the new name. Help text mentions
  // `[deprecated]` so users running `bootstrap-incremental --help`
  // discover the rename.
  program
    .command('bootstrap-incremental')
    .description(
      '[deprecated] Alias for `bootstrap`; will be removed in the next release. Use `kenkeep bootstrap` instead.'
    )
    .option(
      '--from <scope>',
      'narrow discovery to a subdirectory of the repo root (path relative to repo root)'
    )
    .action((opts: { from?: string }) => {
      process.stderr.write(
        "[deprecated] use 'kenkeep bootstrap' instead of 'bootstrap-incremental'\n"
      );
      const launchOpts: Parameters<typeof runBootstrapLauncher>[0] = {};
      if (opts.from !== undefined) launchOpts.from = opts.from;
      const harnessFlag = getHarnessFlag();
      if (harnessFlag !== undefined) launchOpts.harness = harnessFlag;
      runBootstrapLauncher(launchOpts);
    });

  const migrateGroup = program
    .command('migrate')
    .description(
      "Reports pending knowledge-base migrations. Never executes them: migrations run in-host via the kk-migrate skill, which dispatches on this command's output."
    );
  migrateGroup
    .command('status')
    .description(
      'Deterministic, LLM-free migration report: detects the on-disk schema_version and, when migrations are pending, prints the ordered step chain as a JSON document {"current","target","steps":[{"id","from","to","primitives"}]} for the kk-migrate skill to dispatch on, or reports "nothing to do" when already current.'
    )
    .allowExcessArguments(true)
    .action(async () => {
      const code = await runMigrateStatus();
      process.exit(code);
    });
  migrateGroup
    .command('okf-v3')
    .description(
      'Deterministic, LLM-free schema migration primitive for the 2 -> 3 OKF-native step. Mechanically rewrites v2 leaves to v3, migrates folder summaries to the sidecar, rebuilds indexes/ENTRY/GRAPH, and prints a JSON summary.'
    )
    .allowExcessArguments(true)
    .action(async () => {
      const code = await runMigrateOkfV3();
      process.exit(code);
    });

  const packGroup = program
    .command('pack')
    .description('Import and export kenkeep knowledge packs.');
  packGroup
    .command('import')
    .description('Import a validated knowledge pack into an isolated nodes/<name>/ branch.')
    .argument('<source>', 'GitHub owner/repo, GitHub URL, or local .tar.gz path')
    .option('--as <name>', 'destination branch name under nodes/')
    .allowExcessArguments(true)
    .action(async (source: string, opts: { as?: string }) => {
      const flags: Parameters<typeof runPackImportCommand>[1] = {};
      if (opts.as !== undefined) flags.as = opts.as;
      const code = await runPackImportCommand(source, flags);
      process.exit(code);
    });
  packGroup
    .command('export')
    .description('Export the current nodes/ tree as a publishable knowledge pack.')
    .option('--name <name>', 'pack slug, e.g. drupal')
    .option('--version <version>', 'pack version')
    .option('--summary <text>', 'one-line pack summary')
    .option('--homepage <url>', 'optional homepage URL')
    .option('--out <dir>', 'output directory (default: dist)')
    .allowExcessArguments(true)
    .action(
      async (opts: {
        name?: string;
        version?: string;
        summary?: string;
        homepage?: string;
        out?: string;
      }) => {
        const flags: Parameters<typeof runPackExportCommand>[0] = {};
        if (opts.name !== undefined) flags.name = opts.name;
        if (opts.version !== undefined) flags.version = opts.version;
        if (opts.summary !== undefined) flags.summary = opts.summary;
        if (opts.homepage !== undefined) flags.homepage = opts.homepage;
        if (opts.out !== undefined) flags.out = opts.out;
        const code = await runPackExportCommand(flags);
        process.exit(code);
      }
    );

  const placeGroup = program
    .command('place')
    .description(
      'Deterministic, LLM-free primitives for the flat-to-tree (v1->v2) migration step; driven by the kk-migrate skill after `migrate status` reports the step pending. Refuses unless the on-disk knowledge base is at schema_version 1.'
    );
  placeGroup
    .command('inventory')
    .description(
      'Deterministic, LLM-free inventory for the flat-to-tree step: once `migrate status` reports the step pending, prints the flat leaves as a JSON document {"leaves":[{"id","title","kind","tags","summary","relates_to","sourcePath"}]} for the kk-migrate skill to cluster, or reports "nothing to do" when already current. Refuses when the on-disk schema_version is anything other than exactly 1.'
    )
    .allowExcessArguments(true)
    .action(async () => {
      const code = await runPlaceInventory();
      process.exit(code);
    });
  placeGroup
    .command('apply')
    .description(
      'Deterministic placement primitive: applies a caller-supplied placement-and-folders plan {"placements":[{"id","targetFolder"}],"folders":[{"folder","summary"}]} as id-stable, byte-stable relocations and stamps each authored folder summary. Validates every id and folder before any write (a bad plan aborts with zero changes). Reads the plan JSON from --input or stdin. Writes files only; never stages or commits. Prints a placement summary JSON.'
    )
    .option('--input <path>', 'path to the placement-and-folders JSON; reads stdin when omitted')
    .allowExcessArguments(true)
    .action(async (opts: { input?: string }) => {
      const flags: Parameters<typeof runPlaceApply>[0] = {};
      if (opts.input !== undefined) flags.input = opts.input;
      const code = await runPlaceApply(flags);
      process.exit(code);
    });

  const rebalanceGroup = program
    .command('rebalance')
    .description('Deterministic, LLM-free tree rebalance primitives (the final phase of curate).');
  rebalanceGroup
    .command('trigger')
    .description(
      'Deterministic, LLM-free rebalance check: reads Plan 1 per-folder metrics and prints a stable JSON decision {"actions":[{"branch","operation","branches?","topic?"}]} (split-folder/split-leaf/merge/create-branch), or {"actions":[]} when nothing trips past the hysteresis margin so the LLM phase is skipped.'
    )
    .allowExcessArguments(true)
    .action(async () => {
      const code = await runRebalanceTrigger();
      process.exit(code);
    });
  rebalanceGroup
    .command('move')
    .description(
      'Deterministic move primitive: applies a caller-supplied operation plan (split-folder/split-leaf/merge/create-branch) as content-byte-stable, id-stable git renames (split-leaf mints new ids + a redirect), then rebuilds affected index nodes and nodes_hash. Reads the plan JSON from --input or stdin. Writes files only; never stages or commits. Prints a structural summary JSON.'
    )
    .option('--input <path>', 'path to the operation-plan JSON; reads stdin when omitted')
    .allowExcessArguments(true)
    .action(async (opts: { input?: string }) => {
      const flags: Parameters<typeof runRebalanceMove>[0] = {};
      if (opts.input !== undefined) flags.input = opts.input;
      const code = await runRebalanceMove(flags);
      process.exit(code);
    });

  const conflictGroup = program
    .command('conflict')
    .description('Deterministic, LLM-free primitives for the curate conflict-resolution flow.');
  conflictGroup
    .command('prepare')
    .description(
      'Deterministic conflict-prep primitive: reads pending conflict files, computes each conflict default reply (the diff-ratio rules) and the sort/group order, and prints a JSON document the kk-curate skill renders before asking the user. Read-only — never mutates conflicts or asks. Pure Node, no LLM.'
    )
    .allowExcessArguments(true)
    .action(async () => {
      const code = await runConflictPrepareCommand();
      process.exit(code);
    });

  const draftsGroup = program
    .command('drafts')
    .description(
      'Deterministic, LLM-free primitives for the curate parallel-path draft collector.'
    );
  draftsGroup
    .command('collect')
    .description(
      'Deterministic draft collector: reads ${RUN_ID}__*.draft.json under the curator log dir, validates each batch array against a named schema (default curator-output), concatenates survivors to stdout as one JSON array, and reports counts + invalid batches on stderr. A bad batch is skipped, never fatal. Pure Node, no LLM.'
    )
    .requiredOption('--run-id <id>', 'run id whose batch draft files are aggregated')
    .option(
      '--schema <name>',
      'registered schema each batch validates against (default: curator-output)'
    )
    .allowExcessArguments(true)
    .action(async (opts: { runId: string; schema?: string }) => {
      const flags: Parameters<typeof runDraftsCollectCommand>[0] = { runId: opts.runId };
      if (opts.schema !== undefined) flags.schema = opts.schema;
      const code = await runDraftsCollectCommand(flags);
      process.exit(code);
    });

  const nodeGroup = program.command('node').description('Manage kenkeep nodes.');
  nodeGroup
    .command('add')
    .description(
      'Launch the kk-add skill in the active harness (execs `<harness> -p "/kk-add"`). The skill collects kind/title/summary/body/tags interactively and persists via the `node write` primitive.'
    )
    .action(() => {
      const launchOpts: Parameters<typeof runNodeAddLauncher>[0] = {};
      const harnessFlag = getHarnessFlag();
      if (harnessFlag !== undefined) launchOpts.harness = harnessFlag;
      runNodeAddLauncher(launchOpts);
    });
  nodeGroup
    .command('write')
    .description(
      'Headless primitive: atomically write a single node to nodes/<folder>/<id>.md (or nodes/<id>.md at the root when --folder is omitted) with Zod-validated frontmatter and slug-collision resolution. The folder is presentation only; the id is folder-independent. Body read from stdin (default) or --from <path>. Prints the resolved id to stdout. When both --source-doc and --source-hash are passed, also updates bootstrap-state.json per-file hash map.'
    )
    .argument('<kind>', 'node kind: practice or map')
    .argument('<slug>', 'proposed id base (kind prefix added automatically when missing)')
    .option('--title <title>', 'short title (≤ 80 chars)')
    .option('--summary <summary>', 'one-line summary')
    .option('--tags <list>', 'comma-separated tags')
    .option('--relates-to <list>', 'comma-separated node ids (loose cross edges)')
    .option('--depends-on <list>', 'comma-separated node ids this node depends on')
    .option('--confidence <level>', 'low, medium, or high (default: high)')
    .option('--from <path>', 'read body from <path> instead of stdin')
    .option(
      '--folder <relpath>',
      'existing home folder under nodes/ (POSIX-style); omitted/empty lands the leaf at the nodes/ root'
    )
    .option('--source-doc <relpath>', 'source markdown doc (repo-relative); requires --source-hash')
    .option('--source-hash <sha256>', 'sha256 hex digest of --source-doc; requires --source-doc')
    .action(
      async (
        kind: string,
        slug: string,
        opts: {
          title?: string;
          summary?: string;
          tags?: string;
          relatesTo?: string;
          dependsOn?: string;
          confidence?: string;
          from?: string;
          folder?: string;
          sourceDoc?: string;
          sourceHash?: string;
        }
      ) => {
        const flags: Parameters<typeof runNodeWriteCommand>[0]['flags'] = {};
        if (opts.title !== undefined) flags.title = opts.title;
        if (opts.summary !== undefined) flags.summary = opts.summary;
        if (opts.tags !== undefined) flags.tags = opts.tags;
        if (opts.relatesTo !== undefined) flags.relatesTo = opts.relatesTo;
        if (opts.dependsOn !== undefined) flags.dependsOn = opts.dependsOn;
        if (opts.confidence !== undefined) flags.confidence = opts.confidence;
        if (opts.from !== undefined) flags.from = opts.from;
        if (opts.folder !== undefined) flags.folder = opts.folder;
        if (opts.sourceDoc !== undefined) flags.sourceDoc = opts.sourceDoc;
        if (opts.sourceHash !== undefined) flags.sourceHash = opts.sourceHash;
        const code = await runNodeWriteCommand({ kind, slug, flags });
        process.exit(code);
      }
    );

  const indexGroup = program.command('index').description('Manage ENTRY.md and GRAPH.md.');
  indexGroup
    .command('rebuild')
    .description('Regenerate ENTRY.md and GRAPH.md from the current nodes/ tree (deterministic).')
    .option(
      '--stage',
      'after writing, `git add` ENTRY.md and GRAPH.md (no-op outside a git repo)',
      false
    )
    .allowExcessArguments(true)
    .action(async (opts: { stage: boolean }) => {
      const code = await runIndexRebuild(opts);
      process.exit(code);
    });

  const logsGroup = program.command('logs').description('Manage stream-json log files.');
  logsGroup
    .command('prune')
    .description(
      'Delete JSONL log files under .ai/kenkeep/_logs/ older than settings.logsRetentionDays (default 30 days).'
    )
    .action(async () => {
      const code = await runLogsPrune();
      process.exit(code);
    });

  const sessionLogGroup = program.command('session-log').description('Manage session log files.');
  sessionLogGroup
    .command('update-proposals')
    .description(
      'Headless primitive: read proposal JSON from stdin, validate against ProposalOutputSchema, and write results into the session log frontmatter at <path>. Pure Node — no LLM.'
    )
    .argument('<path>', 'path to the session log file')
    .requiredOption('--status <status>', 'proposal status: done or failed')
    .option('--error <message>', 'error message (used with --status failed)')
    .action(async (path: string, opts: { status: string; error?: string }) => {
      const code = await runSessionLogUpdateProposalsCommand({
        path,
        status: opts.status,
        error: opts.error,
      });
      process.exit(code);
    });
  sessionLogGroup
    .command('stage-live')
    .description(
      'Headless primitive: validate live-session proposal JSON from stdin and create or update a session log with proposal_status: done. Pure Node — no LLM.'
    )
    .option('--session-id <uuid>', 'UUID-v4 session id for normal idempotency with later capture')
    .option(
      '--generate-session-id',
      'generate a UUID-v4 fallback session id (degraded idempotency; report in output)'
    )
    .option(
      '--transcript-excerpt <path-or-text>',
      'optional evidence excerpt (file path or inline text); private spans are stripped'
    )
    .action(
      async (opts: {
        sessionId?: string;
        generateSessionId?: boolean;
        transcriptExcerpt?: string;
      }) => {
        const flags: Parameters<typeof runSessionLogStageLiveCommand>[0] = {};
        if (opts.sessionId !== undefined) flags.sessionId = opts.sessionId;
        if (opts.generateSessionId) flags.generateSessionId = true;
        if (opts.transcriptExcerpt !== undefined) flags.transcriptExcerpt = opts.transcriptExcerpt;
        const code = await runSessionLogStageLiveCommand(flags);
        process.exit(code);
      }
    );

  program
    .command('finddocs')
    .description(
      'Deterministically enumerate candidate markdown files for the kk, applying .gitignore, .kkignore, and the built-in static-skip list. Read-only; emits one `+ <relpath>` line per survivor.'
    )
    .option(
      '--from <scope>',
      'narrow discovery to a subdirectory of the repo root (path relative to repo root)'
    )
    .option(
      '--with-hashes',
      'append a tab-separated SHA-256 hex digest to each line so callers can compare against bootstrap-state.json',
      false
    )
    .action(async (opts: { from?: string; withHashes: boolean }) => {
      const flags: Parameters<typeof runFindDocsCommand>[0] = {};
      if (opts.from !== undefined) flags.from = opts.from;
      if (opts.withHashes) flags.withHashes = true;
      const code = await runFindDocsCommand(flags);
      process.exit(code);
    });

  program
    .command('schema')
    .description(
      `Print the JSON Schema for a named contract (${SCHEMA_NAMES.join(', ')}), generated from the Zod definitions in src/lib/schemas.ts. Pure Node — no LLM.`
    )
    .argument('<name>', 'registered schema name')
    .allowExcessArguments(true)
    .action(async (name: string) => {
      const code = await runSchemaCommand({ name });
      process.exit(code);
    });

  program
    .command('validate')
    .description(
      'Validate a JSON or YAML artifact against a named schema and print path-referenced errors. Reads the artifact from <file> or stdin (or `-`). Drives the skills’ produce → validate → fix loop. Pure Node — no LLM.'
    )
    .argument('<name>', 'registered schema name')
    .argument('[file]', 'artifact path; reads stdin when omitted or `-`')
    .allowExcessArguments(true)
    .action(async (name: string, file: string | undefined) => {
      const flags: Parameters<typeof runValidateCommand>[0] = { name };
      if (file !== undefined) flags.file = file;
      const code = await runValidateCommand(flags);
      process.exit(code);
    });

  await program.parseAsync(process.argv);
}

main().catch((err: unknown) => {
  if (err instanceof Error) {
    log.error(err.message);
  } else {
    log.error(String(err));
  }
  process.exit(1);
});
